/**
 * LanguageContext
 * Provides language context and translation functionality
 */

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import { logger } from '../helpers/logger.helper'
import { Platform } from 'react-native'
import { useNativeSdkInit } from '../shared/contexts/NativeSdkInitContext'
import {
  coerceToEnabledLanguage,
  getEnabledLanguages,
  isLanguageEnabled,
} from './helpers/languageAvailability.helper'
import {
  Language,
  translations,
  supportedLanguages,
  Translations,
} from './translations'

export interface LanguageContextValue {
  language: Language
  setLanguage: (lang: Language) => Promise<void>
  t: typeof translations.en
  supportedLanguages: typeof supportedLanguages
}

export const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined
)

const LANGUAGE_STORAGE_KEY = '@zporti:language'

function getRuntimeHostname(): string | null {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.location.hostname
  }
  return null
}

/**
 * Detects user's preferred language from browser/system
 */
const detectLanguage = (enabledLanguages: readonly Language[]): Language => {
  if (
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    navigator.language
  ) {
    const browserLang = navigator.language.toLowerCase()
    // Check for Chinese variants (zh-CN, zh-TW, etc.)
    if (browserLang.startsWith('zh') && enabledLanguages.includes('zh')) {
      return 'zh'
    }
    // Check for other languages
    const langCode = browserLang.split('-')[0] as Language
    if (enabledLanguages.includes(langCode)) {
      return langCode
    }
  }
  return 'en' // Default to English
}

interface LanguageProviderProps {
  children: React.ReactNode
}

function getAsyncStorage() {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require('@react-native-async-storage/async-storage').default as {
    getItem: (key: string) => Promise<string | null>
    setItem: (key: string, value: string) => Promise<void>
  }
}

async function readLanguagePreference(): Promise<string | null> {
  return getAsyncStorage().getItem(LANGUAGE_STORAGE_KEY)
}

/**
 * Web-only synchronous read of the stored preference.
 *
 * AsyncStorage is backed by localStorage on web but exposes only a promise, which forces
 * the provider to withhold children for a tick. During Expo's static export that tick
 * never comes, so the whole app tree rendered as an empty page. Reading localStorage
 * directly lets the first render already know the language — no gate, no flash.
 *
 * Returns null on native, during static render (no window), or if storage is unavailable
 * (Safari private mode throws on access).
 */
function readLanguagePreferenceSyncOnWeb(): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null
  }
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  } catch {
    return null
  }
}

async function writeLanguagePreference(value: string): Promise<void> {
  await getAsyncStorage().setItem(LANGUAGE_STORAGE_KEY, value)
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const { bridgeReady } = useNativeSdkInit()
  // Null on the first render, even on the client: the static HTML was rendered without a
  // hostname, so reading window.location here would make the first client render disagree
  // with the server (different enabled-language set -> React hydration error #418).
  // The real hostname is adopted on the next tick, once hydration is done.
  const [runtimeHostname, setRuntimeHostname] = useState<string | null>(null)
  useEffect(() => {
    setRuntimeHostname(getRuntimeHostname())
  }, [])
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = readLanguagePreferenceSyncOnWeb()
    if (stored && isLanguageEnabled(stored as Language, process.env, runtimeHostname)) {
      return stored as Language
    }
    return 'en'
  })
  // Web renders immediately (the language is already known synchronously, and static
  // export has no storage to wait for). Native keeps the gate so no flash of English
  // appears while AsyncStorage resolves.
  const [isLoaded, setIsLoaded] = useState(Platform.OS === 'web')
  const enabledLanguages = useMemo(
    () => getEnabledLanguages(process.env as Record<string, string | undefined>, runtimeHostname),
    [runtimeHostname],
  )
  const availableSupportedLanguages = useMemo(
    () => supportedLanguages.filter((lang) => enabledLanguages.includes(lang.code)),
    [enabledLanguages],
  )

  // iOS: defer AsyncStorage until native bridge quiet period (see NativeSdkInitContext).
  useEffect(() => {
    if (Platform.OS === 'ios' && !bridgeReady) return

    const loadLanguage = async () => {
      try {
        const savedLanguage = await readLanguagePreference()
        if (savedLanguage && isLanguageEnabled(savedLanguage as Language, process.env, runtimeHostname)) {
          setLanguageState(savedLanguage as Language)
        } else if (savedLanguage) {
          setLanguageState('en')
          await writeLanguagePreference('en')
        } else {
          const detected = detectLanguage(enabledLanguages)
          setLanguageState(coerceToEnabledLanguage(detected, process.env, runtimeHostname))
        }
      } catch (error) {
        logger.error('Failed to load language preference', { error })
        setLanguageState(detectLanguage(enabledLanguages))
      } finally {
        setIsLoaded(true)
      }
    }

    void loadLanguage()
  }, [bridgeReady, enabledLanguages, runtimeHostname])

  const setLanguage = useCallback(async (lang: Language) => {
    if (!isLanguageEnabled(lang, process.env, runtimeHostname)) {
      return
    }

    try {
      await writeLanguagePreference(lang)
      setLanguageState(lang)
    } catch (error) {
      logger.error('Failed to save language preference', { error })
    }
  }, [runtimeHostname])

  const value: LanguageContextValue = {
    language,
    setLanguage,
    t: translations[language] as Translations,
    supportedLanguages: availableSupportedLanguages,
  }

  // Native only: hold children until AsyncStorage resolves, to prevent a flash of
  // English. On web isLoaded starts true — see readLanguagePreferenceSyncOnWeb.
  if (!isLoaded) {
    return null
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}
